import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection, getDocs, query, where,
  doc, addDoc, getDoc, updateDoc
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';

// ─── Question definitions (must match rubric template) ────────
const QUESTIONS = [
  { id: 'q01', dimension: 'Problem Validation',  text: 'Have you validated a real problem exists that your solution addresses?',
    options: [{ label: 'No validation yet', score: 0 }, { label: 'Informal validation through discussions with potential users', score: 2 }, { label: 'User surveys, interviews or market research to confirm the problem', score: 5 }, { label: 'Users are actively seeking solutions like yours', score: 10 }] },
  { id: 'q02', dimension: 'Product Development', text: 'What is the current status of your Minimum Viable Product (MVP)?',
    options: [{ label: 'No MVP developed yet', score: 0 }, { label: 'MVP in early development', score: 2 }, { label: 'MVP launched with early user feedback', score: 5 }, { label: 'MVP iterated based on significant user engagement', score: 10 }] },
  { id: 'q03', dimension: 'User Adoption',       text: 'How many active users or early adopters does your solution have?',
    options: [{ label: 'Less than 50 users', score: 0 }, { label: '50–200 users', score: 2 }, { label: '200–500 users', score: 5 }, { label: '500+ users', score: 10 }] },
  { id: 'q04', dimension: 'Funding',             text: 'Have you secured any early funding?',
    options: [{ label: 'No funding yet', score: 0 }, { label: 'Small grants or personal funds', score: 2 }, { label: 'Initial investment or accelerator', score: 5 }, { label: 'Multiple funding sources', score: 10 }] },
  { id: 'q05', dimension: 'Product-Market Fit',  text: 'How well is your product resonating with users?',
    options: [{ label: 'Low engagement or inconsistent usage', score: 0 }, { label: 'Moderate engagement but no repeat usage', score: 3 }, { label: 'High engagement with repeat usage', score: 7 }, { label: 'Strong demand with organic referrals', score: 10 }] },
  { id: 'q06', dimension: 'Retention',           text: 'What percentage of users return to use your solution?',
    options: [{ label: 'Less than 20% retention', score: 0 }, { label: '20–50% retention', score: 3 }, { label: '50–70% retention', score: 7 }, { label: 'Above 70% retention', score: 10 }] },
  { id: 'q07', dimension: 'Revenue',             text: 'What revenue traction do you currently have?',
    options: [{ label: 'No revenue yet', score: 0 }, { label: 'Some one-time sales, small transactions', score: 3 }, { label: 'Generating monthly revenue between ETB2k–ETB10k', score: 7 }, { label: 'Consistent revenue over ETB10k per month', score: 10 }] },
  { id: 'q08', dimension: 'Partnerships',        text: 'Have you established any B2B or institutional partnerships?',
    options: [{ label: 'No partnerships', score: 0 }, { label: 'Initial discussions with potential partners', score: 3 }, { label: 'One or two small partnerships', score: 7 }, { label: 'Multiple high level partnerships', score: 10 }] },
  { id: 'q09', dimension: 'Scale',               text: 'How many active users do you currently serve?',
    options: [{ label: 'Less than 1k users', score: 0 }, { label: '1k–10k users', score: 3 }, { label: '10k–50k users', score: 7 }, { label: '50k+ users', score: 10 }] },
  { id: 'q10', dimension: 'Market Reach',        text: 'What is your regional market penetration?',
    options: [{ label: 'Limited to one locality or pilot users', score: 0 }, { label: 'Expanded to multiple locations but not scaling', score: 3 }, { label: 'Operating in 2–5 regions', score: 7 }, { label: 'Operating in 5+ regions', score: 10 }] },
  { id: 'q11', dimension: 'Revenue Range',       text: 'What is your revenue range per month?',
    options: [{ label: 'Less than ETB10k', score: 0 }, { label: 'ETB10k–ETB50k', score: 3 }, { label: 'ETB50k–ETB200k', score: 7 }, { label: 'ETB200k+', score: 10 }] },
  { id: 'q12', dimension: 'Customer Acquisition',text: 'How robust is your customer acquisition strategy?',
    options: [{ label: 'No structured marketing or sales strategy', score: 0 }, { label: 'Some organic growth through word-of-mouth', score: 3 }, { label: 'Paid marketing with measurable results', score: 7 }, { label: 'Scalable acquisition strategy driving consistent growth', score: 10 }] },
  { id: 'q13', dimension: 'Total Users',         text: 'What is your total user base?',
    options: [{ label: 'Less than 10k users', score: 0 }, { label: '10k–100k users', score: 5 }, { label: '100k–500k users', score: 7 }, { label: '500k+ users', score: 10 }] },
  { id: 'q14', dimension: 'Geographic Expansion',text: 'Have you expanded outside your initial market?',
    options: [{ label: 'Still in initial market', score: 0 }, { label: 'Exploring opportunities for expansion', score: 3 }, { label: 'Expanded to at least one new country', score: 7 }, { label: 'Operating in multiple countries', score: 10 }] },
  { id: 'q15', dimension: 'Annual Revenue',      text: 'What is your annual revenue?',
    options: [{ label: 'Less than ETB500k', score: 0 }, { label: 'ETB500k–ETB1M', score: 5 }, { label: 'ETB1M–ETB3M', score: 7 }, { label: 'Above ETB3M', score: 10 }] },
  { id: 'q16', dimension: 'Government Integration', text: 'What level of institutional/government integration do you have?',
    options: [{ label: 'No government or institutional support', score: 0 }, { label: 'Engaged in discussions', score: 3 }, { label: 'Government adoption or funding secured', score: 7 }, { label: 'Fully integrated into government policies or national programs', score: 10 }] },
];

const STAGES = [
  { stage: 1, label: 'Problem-Solution Fit',        min: 0,   max: 40  },
  { stage: 2, label: 'Product-Market Fit',           min: 41,  max: 80  },
  { stage: 3, label: 'Early Growth & Monetization',  min: 81,  max: 120 },
  { stage: 4, label: 'Scaling & Regional Expansion', min: 121, max: 160 },
];

const STAGE_COLORS = {
  'Problem-Solution Fit':        '#e8522a',
  'Product-Market Fit':          '#f4a261',
  'Early Growth & Monetization': '#4a7c3f',
  'Scaling & Regional Expansion':'#2a9d8f',
};

// ─── Score computation (mirrors Cloud Function logic) ─────────
function computeScore(answers) {
  const total = Object.values(answers).reduce((sum, s) => sum + (s || 0), 0);
  const stage = STAGES.find(s => total >= s.min && total <= s.max) || STAGES[STAGES.length - 1];
  const bandRange = stage.max - stage.min;
  const posInBand = total - stage.min;
  const progress  = bandRange > 0 ? Math.round((posInBand / bandRange) * 100) : 100;

  // Dimension scores
  const dims = {};
  QUESTIONS.forEach(q => {
    if (answers[q.id] != null) {
      if (!dims[q.dimension]) dims[q.dimension] = { sum: 0, count: 0, max: 0 };
      dims[q.dimension].sum   += answers[q.id];
      dims[q.dimension].max   += 10;
      dims[q.dimension].count += 1;
    }
  });

  const dimensionScores = Object.entries(dims).map(([name, d]) => ({
    name,
    score: d.sum,
    max: d.max,
    pct: Math.round((d.sum / d.max) * 100),
  }));

  return { total, stage, progress, dimensionScores };
}

// ─── Upgrade label ────────────────────────────────────────────
function upgradeLabel(progress) {
  if (progress <= 25) return 'Low End';
  if (progress <= 50) return 'Lower Mid';
  if (progress <= 75) return 'Upper Mid';
  return 'Upper';
}

// ════════════════════════════════════════════════════════════════
export default function AssessmentPage() {
  const { profile } = useAuth();

  const navigate = useNavigate();

  const [company, setCompany]           = useState(null);
  const [companyDocId, setCompanyDocId] = useState(null);
  const [program, setProgram]           = useState(null);
  const [currentPeriodAssessment, setCurrentPeriodAssessment] = useState(null);
  const [lastAssessment, setLastAssessment] = useState(null);
  const [allAssessments, setAllAssessments] = useState([]);
  const [existingTypes, setExistingTypes]   = useState([]); // which of baseline/target already exist

  // New-assessment picker state
  const [pickerType, setPickerType] = useState('');
  const [pickerDate, setPickerDate] = useState(() => new Date().toISOString().slice(0,10));

  const [answers, setAnswers]     = useState({});
  const [mode, setMode]           = useState('loading'); // loading | picker | form | review | result | locked
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]         = useState(null);
  const [result, setResult]       = useState(null);

  const loadAssessments = useCallback(async (cDocId, cData) => {
    const aSnap = await getDocs(collection(db, 'companies', cDocId, 'assessments'));
    const all = aSnap.docs.map(d => ({ id: d.id, ...d.data() }))
      .sort((a,b) => (b.assessmentDate?.seconds||0) - (a.assessmentDate?.seconds||0));
    setAllAssessments(all);

    const existing = [...new Set(all.map(a => a.type))];
    setExistingTypes(existing);

    // Any assessment still pending coach validation takes priority — show that first
    const pending = all.find(a => a.status !== 'validated');
    if (pending) {
      setCurrentPeriodAssessment(pending);
      setAnswers(pending.questionScores || {});
      setMode('review');
      if (pending.coachNotes && pending.coachNotesUpdatedAt) {
        const updatedAt = pending.coachNotesUpdatedAt?.toDate ? pending.coachNotesUpdatedAt.toDate() : new Date(pending.coachNotesUpdatedAt);
        const readAt = pending.coachNotesReadAt?.toDate ? pending.coachNotesReadAt.toDate() : null;
        if (!readAt || updatedAt > readAt) {
          updateDoc(doc(db, 'companies', cDocId, 'assessments', pending.id), { coachNotesReadAt: new Date() }).catch(() => {});
        }
      }
      return;
    }

    // Nothing pending — show the most recently validated one alongside the option to start a new one
    const latestValidated = all.find(a => a.status === 'validated') || null;
    setCurrentPeriodAssessment(latestValidated);
    setLastAssessment(all[0] || null);
    setMode(latestValidated ? 'locked' : 'picker');
  }, []);

  // ── Load company, program, assessments ──────────────────────
  useEffect(() => {
    if (!profile?.uid) return;
    async function load() {
      try {
        // Get company
        const cSnap = await getDocs(
          query(collection(db, 'companies'), where('memberUids', 'array-contains', profile.uid))
        );
        if (cSnap.empty) { setMode('error'); return; }
        const cDoc  = cSnap.docs[0];
        const cData = cDoc.data();
        setCompany(cData);
        setCompanyDocId(cDoc.id);

        // Program is used only for display labels now (no period gating)
        try {
          const pSnap = await getDoc(doc(db, 'programs', cData.programId));
          setProgram(pSnap.data() || null);
        } catch { setProgram(null); }

        await loadAssessments(cDoc.id, cData);
      } catch (err) {
        console.error(err);
        setError(err.message);
        setMode('error');
      }
    }
    load();
  }, [profile, loadAssessments]);

  // ── Start a new assessment from the picker ───────────────────
  const startNewAssessment = () => {
    if (!pickerType) return;
    // Pre-populate from the most recent assessment of any type, if one exists
    const last = allAssessments[0] || null;
    setLastAssessment(last);
    setAnswers(last?.questionScores || {});
    setMode('form');
  };

  // ── Handle answer selection ───────────────────────────────
  const handleAnswer = useCallback((qId, score) => {
    setAnswers(prev => ({ ...prev, [qId]: score }));
  }, []);

  // ── Submit ────────────────────────────────────────────────
  const handleSubmit = async () => {
    const unanswered = QUESTIONS.filter(q => answers[q.id] == null);
    if (unanswered.length > 0) {
      setError(`Please answer all questions. ${unanswered.length} question${unanswered.length > 1 ? 's' : ''} remaining.`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const { total, stage, progress, dimensionScores } = computeScore(answers);
      const upgrade = upgradeLabel(progress);

      // If we're editing a pre-existing, not-yet-validated submission, update it in place
      // rather than creating a duplicate. Otherwise this is a brand new assessment.
      const isEditingExisting = currentPeriodAssessment && currentPeriodAssessment.status !== 'validated'
        && currentPeriodAssessment.type === pickerType;

      const assessmentData = {
        companyId:      companyDocId,
        programId:      company.programId,
        rubricTemplateId: `tmpl_${company.programId}`,
        type:           pickerType || 'periodic',
        period:         program?.currentPeriod || null, // retained for labelling/reporting only — never gates creation
        assessmentDate: new Date(pickerDate),
        questionScores: answers,
        totalScore:     total,
        stage:          stage.label,
        stageProgress:  progress,
        stageUpgrade:   upgrade,
        dimensionScores,
        status:         'submitted',
        submittedBy:    profile.uid,
        updatedAt:      new Date(),
        ...(isEditingExisting ? {} : { createdAt: new Date() }),
      };

      let finalId;
      if (isEditingExisting) {
        await updateDoc(doc(db, 'companies', companyDocId, 'assessments', currentPeriodAssessment.id), assessmentData);
        finalId = currentPeriodAssessment.id;
      } else {
        const ref = await addDoc(
          collection(db, 'companies', companyDocId, 'assessments'),
          assessmentData
        );
        finalId = ref.id;
      }

      // Note: company latest score snapshot is updated server-side via Cloud Function
      setCurrentPeriodAssessment({ id: finalId, ...assessmentData });
      setResult({ total, stage, progress, upgrade, dimensionScores });
      setMode('success');
      window.scrollTo({ top: 0, behavior: 'smooth' });

    } catch (err) {
      console.error(err);
      setError('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Render helpers ───────────────────────────────────────
  const completedCount = QUESTIONS.filter(q => answers[q.id] != null).length;
  const liveScore      = computeScore(answers);

  // ════════════════════════════════════════════════════════════
  return (
    <DashboardLayout>
      <div className="gm-page gm-assessment-page">

        {/* ── Loading ── */}
        {mode === 'loading' && (
          <div className="gm-loading"><div className="gm-spinner" /></div>
        )}

        {/* ── Error ── */}
        {mode === 'error' && (
          <div className="gm-alert gm-alert-error" style={{ marginTop: 24 }}>
            {error || 'Unable to load assessment. Contact your programme coordinator.'}
          </div>
        )}



        {/* ── Locked (coach validated) — also offers starting a new assessment ── */}
        {mode === 'locked' && currentPeriodAssessment && (
          <>
            <LockedView assessment={currentPeriodAssessment} company={company} program={program} />
            <NewAssessmentPicker
              existingTypes={existingTypes}
              pickerType={pickerType} setPickerType={setPickerType}
              pickerDate={pickerDate} setPickerDate={setPickerDate}
              onStart={startNewAssessment}
            />
          </>
        )}

        {/* ── Picker (no assessment exists yet at all) ── */}
        {mode === 'picker' && (
          <NewAssessmentPicker
            existingTypes={existingTypes}
            pickerType={pickerType} setPickerType={setPickerType}
            pickerDate={pickerDate} setPickerDate={setPickerDate}
            onStart={startNewAssessment}
          />
        )}

        {/* ── Coach feedback on any assessment ── */}
        {mode !== 'loading' && mode !== 'error' && mode !== 'form' && mode !== 'picker' && (
          <CoachFeedbackPanel
            companyDocId={companyDocId}
            profile={profile}
          />
        )}

        {/* ── Assessment history ── */}
        {mode !== 'loading' && mode !== 'error' && mode !== 'form' && companyDocId && (
          <AssessmentHistory companyDocId={companyDocId} currentPeriodId={currentPeriodAssessment?.id} />
        )}

        {/* ── Review (submitted, awaiting validation) ── */}
        {mode === 'review' && currentPeriodAssessment && (
          <ReviewView
            assessment={currentPeriodAssessment}
            company={company}
            program={program}
            onEdit={() => { setPickerType(currentPeriodAssessment?.type || 'periodic'); setMode('form'); }}
          />
        )}

        {/* ── Success (just submitted, redirecting) ── */}
        {mode === 'success' && result && (
          <SuccessScreen
            result={result}
            company={company}
            onGoToDashboard={() => navigate('/scaling/company')}
          />
        )}

        {/* ── Form ── */}
        {mode === 'form' && (
          <>
            <div className="gm-page-header">
              <div>
                <h1 className="gm-page-title">{({ baseline: 'Baseline', periodic: 'Periodic', target: 'Target' }[pickerType]) || 'Scaling'} Assessment</h1>
                <p className="gm-page-sub">
                  {company?.name} · {pickerDate}
                  {lastAssessment && ' · Pre-filled from your last submission'}
                </p>
              </div>
              {/* Live stage badge — no score shown */}
              <div className="gm-assessment-live-score">
                <div className="gm-assessment-live-stage" style={{ color: STAGE_COLORS[liveScore.stage?.label] }}>
                  {liveScore.stage?.label}
                </div>
                <div className="gm-assessment-live-label">{liveScore.progress}% through stage</div>
              </div>
            </div>

            {error && (
              <div className="gm-alert gm-alert-error" style={{ marginBottom: 24 }}>
                {error}
              </div>
            )}

            {/* Progress indicator */}
            <div className="gm-assessment-progress-bar">
              <div className="gm-assessment-progress-track">
                <div
                  className="gm-assessment-progress-fill"
                  style={{ width: `${(completedCount / QUESTIONS.length) * 100}%` }}
                />
              </div>
              <span className="gm-assessment-progress-label">
                {completedCount} of {QUESTIONS.length} answered
              </span>
            </div>

            {/* Questions grouped by dimension */}
            {groupByDimension(QUESTIONS).map(({ dimension, questions }) => (
              <div key={dimension} className="gm-assessment-dimension">
                <h2 className="gm-assessment-dimension-title">{dimension}</h2>
                {questions.map((q, idx) => (
                  <QuestionCard
                    key={q.id}
                    question={q}
                    index={QUESTIONS.indexOf(q) + 1}
                    selected={answers[q.id]}
                    onChange={(score) => handleAnswer(q.id, score)}
                  />
                ))}
              </div>
            ))}

            {/* Submit */}
            <div className="gm-assessment-submit-area">
              {completedCount < QUESTIONS.length && (
                <p className="gm-assessment-remaining">
                  {QUESTIONS.length - completedCount} question{QUESTIONS.length - completedCount !== 1 ? 's' : ''} remaining before you can submit.
                </p>
              )}
              <button
                className="gm-btn gm-btn-primary"
                onClick={handleSubmit}
                disabled={submitting || completedCount < QUESTIONS.length}
              >
                {submitting ? 'Submitting…' : 'Submit Assessment'}
              </button>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

// ─── Question Card ────────────────────────────────────────────
function QuestionCard({ question, index, selected, onChange }) {
  return (
    <div className={`gm-question-card ${selected != null ? 'gm-question-card--answered' : ''}`}>
      <div className="gm-question-header">
        <span className="gm-question-number">Q{String(index).padStart(2,'0')}</span>
        <p className="gm-question-text">{question.text}</p>
      </div>
      <div className="gm-question-options">
        {question.options.map((opt) => (
          <label
            key={opt.score}
            className={`gm-option ${selected === opt.score ? 'gm-option--selected' : ''}`}
          >
            <input
              type="radio"
              name={question.id}
              value={opt.score}
              checked={selected === opt.score}
              onChange={() => onChange(opt.score)}
            />
            <div className="gm-option-content">
              <span className="gm-option-label">{opt.label}</span>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}


// ─── Success screen (post-submission) ────────────────────────
function SuccessScreen({ result, company, onGoToDashboard }) {
  const { stage, progress, upgrade } = result;
  const color = STAGE_COLORS[stage?.label] || '#4a5481';

  // Auto-redirect after 4 seconds
  React.useEffect(() => {
    const timer = setTimeout(onGoToDashboard, 4000);
    return () => clearTimeout(timer);
  }, [onGoToDashboard]);

  return (
    <div className="gm-result gm-success-screen">
      <div className="gm-success-icon">✓</div>
      <h1 className="gm-page-title">Assessment submitted</h1>
      <p className="gm-page-sub" style={{ marginTop: 8, maxWidth: 480 }}>
        Your responses have been saved for <strong>Q2-2026</strong>. Your coach will review and validate your results shortly.
      </p>

      <div className="gm-success-stage-card" style={{ borderColor: color }}>
        <div className="gm-success-stage-label">Your current stage</div>
        <div className="gm-success-stage-name" style={{ color }}>{stage?.label}</div>
        <div className="gm-success-stage-sub">{upgrade} · {progress}% through this stage</div>
        <div className="gm-mini-progress" style={{ marginTop: 12 }}>
          <div className="gm-mini-progress-fill"
            style={{ width: `${progress}%`, background: color }} />
        </div>
      </div>

      <p className="gm-success-redirect-note">Returning to your dashboard in a moment…</p>

      <button className="gm-btn gm-btn-primary" onClick={onGoToDashboard}>
        Go to dashboard now
      </button>
    </div>
  );
}


// ─── Review view (submitted, awaiting validation) ─────────────
function ReviewView({ assessment, company, program, onEdit }) {
  return (
    <div className="gm-result">
      <div className="gm-page-header">
        <div>
          <h1 className="gm-page-title">Assessment submitted</h1>
          <p className="gm-page-sub">
            {({ baseline: 'Baseline', periodic: 'Periodic', target: 'Target' }[assessment.type]) || 'Assessment'}
            {assessment.assessmentDate && (' · ' + (assessment.assessmentDate?.toDate ? assessment.assessmentDate.toDate() : new Date(assessment.assessmentDate)).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' }))}
            {' · Awaiting coach validation'}
          </p>
        </div>
        <span className="gm-status-badge gm-status-badge--pending">Pending review</span>
      </div>

      <div className="gm-stage-hero" style={{ borderColor: STAGE_COLORS[assessment.stage] || '#4a5481' }}>
        <div className="gm-stage-hero-left">
          <div className="gm-stage-hero-label">Current stage</div>
          <div className="gm-stage-hero-name" style={{ color: STAGE_COLORS[assessment.stage] || '#4a5481' }}>{assessment.stage}</div>
          <div className="gm-stage-hero-upgrade">{assessment.stageUpgrade}</div>
        </div>
        <div className="gm-stage-hero-right">
          <div className="gm-stage-hero-progress-label">Progress within stage</div>
          <div className="gm-stage-hero-pct" style={{ color: STAGE_COLORS[assessment.stage] || '#4a5481' }}>{assessment.stageProgress}%</div>
          <div className="gm-mini-progress" style={{ width: 160 }}>
            <div className="gm-mini-progress-fill" style={{ width: `${assessment.stageProgress||0}%`, background: STAGE_COLORS[assessment.stage] || '#4a5481' }} />
          </div>
        </div>
      </div>

      <div className="gm-section">
        <h2 className="gm-section-title">Your answers</h2>
        {QUESTIONS.map((q, idx) => (
          <div key={q.id} className="gm-answer-row">
            <span className="gm-answer-q">Q{String(idx+1).padStart(2,'0')} {q.text}</span>
            <span className="gm-answer-v">
              {q.options.find(o => o.score === assessment.questionScores?.[q.id])?.label || '—'}
            </span>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 24 }}>
        <button className="gm-btn gm-btn-outline gm-btn-sm" onClick={onEdit}>
          Edit before validation
        </button>
      </div>
    </div>
  );
}

// ─── Locked view (coach validated) ───────────────────────────
function LockedView({ assessment, company, program }) {
  const color = STAGE_COLORS[assessment.stage] || '#4a5481';
  return (
    <div className="gm-result">
      <div className="gm-page-header">
        <div>
          <h1 className="gm-page-title">Assessment validated</h1>
          <p className="gm-page-sub">
            {({ baseline: 'Baseline', periodic: 'Periodic', target: 'Target' }[assessment.type]) || 'Assessment'}
            {assessment.assessmentDate && (' · ' + (assessment.assessmentDate?.toDate ? assessment.assessmentDate.toDate() : new Date(assessment.assessmentDate)).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' }))}
            {' · Reviewed by your coach'}
          </p>
        </div>
        <span className="gm-status-badge gm-status-badge--validated">Validated</span>
      </div>

      <div className="gm-stage-hero" style={{ borderColor: color }}>
        <div className="gm-stage-hero-left">
          <div className="gm-stage-hero-label">Current stage</div>
          <div className="gm-stage-hero-name" style={{ color }}>{assessment.stage}</div>
          <div className="gm-stage-hero-upgrade">{assessment.stageUpgrade}</div>
        </div>
        <div className="gm-stage-hero-right">
          <div className="gm-stage-hero-progress-label">Progress within stage</div>
          <div className="gm-stage-hero-pct" style={{ color }}>{assessment.stageProgress}%</div>
          <div className="gm-mini-progress" style={{ width: 160 }}>
            <div className="gm-mini-progress-fill" style={{ width: `${assessment.stageProgress||0}%`, background: color }} />
          </div>
        </div>
      </div>

      {assessment.coachNotes && (
        <div className="gm-coach-notes gm-coach-notes--highlighted" style={{ marginTop: 16 }}>
          <h3>💬 Feedback from your coach</h3>
          <p style={{ whiteSpace: 'pre-wrap' }}>{assessment.coachNotes}</p>
        </div>
      )}
    </div>
  );
}

// ─── Group questions by dimension ────────────────────────────
function groupByDimension(questions) {
  const map = {};
  questions.forEach(q => {
    if (!map[q.dimension]) map[q.dimension] = { dimension: q.dimension, questions: [] };
    map[q.dimension].questions.push(q);
  });
  return Object.values(map);
}

// ─── Coach Feedback Panel ─────────────────────────────────────
// Shows all coach notes across all assessments for this company
function CoachFeedbackPanel({ companyDocId, profile }) {
  const [feedbackItems, setFeedbackItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyDocId) return;
    async function load() {
      try {
        const snap = await getDocs(collection(db, 'companies', companyDocId, 'assessments'));
        const items = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(a => a.coachNotes)
          .sort((a, b) => {
            const da = a.coachNotesUpdatedAt?.toDate ? a.coachNotesUpdatedAt.toDate() : new Date(0);
            const db2 = b.coachNotesUpdatedAt?.toDate ? b.coachNotesUpdatedAt.toDate() : new Date(0);
            return db2 - da;
          });
        setFeedbackItems(items);

        // Mark all as read
        for (const item of items) {
          if (item.coachNotesUpdatedAt) {
            const updatedAt = item.coachNotesUpdatedAt?.toDate ? item.coachNotesUpdatedAt.toDate() : new Date(item.coachNotesUpdatedAt);
            const readAt    = item.coachNotesReadAt?.toDate   ? item.coachNotesReadAt.toDate()    : null;
            if (!readAt || updatedAt > readAt) {
              updateDoc(doc(db, 'companies', companyDocId, 'assessments', item.id), {
                coachNotesReadAt: new Date()
              }).catch(() => {});
            }
          }
        }
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    }
    load();
  }, [companyDocId]);

  if (loading || feedbackItems.length === 0) return null;

  const TYPE_LABELS = { baseline: 'Baseline assessment', periodic: 'Periodic assessment', target: 'Target assessment' };

  return (
    <div className="gm-section">
      <h2 className="gm-section-title">Coach feedback</h2>
      {feedbackItems.map(item => {
        const isUnread = (() => {
          if (!item.coachNotesUpdatedAt) return false;
          const updatedAt = item.coachNotesUpdatedAt?.toDate ? item.coachNotesUpdatedAt.toDate() : new Date(item.coachNotesUpdatedAt);
          const readAt    = item.coachNotesReadAt?.toDate   ? item.coachNotesReadAt.toDate()    : null;
          return !readAt || updatedAt > readAt;
        })();

        return (
          <div key={item.id} className={`gm-coach-notes gm-coach-notes--highlighted`}
            style={{ marginBottom: 12, borderColor: isUnread ? '#93c5fd' : undefined }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h3>💬 {TYPE_LABELS[item.type] || 'Assessment'} feedback</h3>
              {isUnread && <span className="gm-pending-badge" style={{ background: '#eff6ff', color: '#1d4ed8', borderColor: '#93c5fd' }}>New</span>}
            </div>
            <p style={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem', lineHeight: 1.6 }}>{item.coachNotes}</p>
          </div>
        );
      })}
    </div>
  );
}

// ─── Assessment History ───────────────────────────────────────
const HISTORY_TYPE_ORDER  = { baseline: 0, periodic: 1, target: 2 };
const HISTORY_TYPE_LABELS = { baseline: 'Baseline', periodic: 'Periodic', target: 'Target' };

function AssessmentHistory({ companyDocId, currentPeriodId }) {
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [expanded, setExpanded]       = useState({});

  useEffect(() => {
    if (!companyDocId) return;
    async function load() {
      try {
        const snap = await getDocs(collection(db, 'companies', companyDocId, 'assessments'));
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => {
            if (a.type !== b.type) return HISTORY_TYPE_ORDER[a.type] - HISTORY_TYPE_ORDER[b.type];
            const da = a.assessmentDate?.toDate ? a.assessmentDate.toDate() : new Date(a.assessmentDate || 0);
            const db2 = b.assessmentDate?.toDate ? b.assessmentDate.toDate() : new Date(b.assessmentDate || 0);
            return db2 - da;
          });
        setAssessments(all);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    }
    load();
  }, [companyDocId]);

  if (loading) return null;
  if (assessments.length === 0) return null;

  const formatDate = (ts) => {
    if (!ts) return '—';
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div className="gm-section" style={{ marginTop: 32 }}>
      <h2 className="gm-section-title">Assessment history</h2>
      {assessments.map(a => {
        const isCurrent = a.id === currentPeriodId;
        const isExpanded = expanded[a.id];
        const color = STAGE_COLORS[a.stage] || '#4a5481';
        const date = a.assessmentDate?.toDate ? a.assessmentDate.toDate() : new Date(a.assessmentDate || 0);
        const isValidated = a.status === 'validated';

        return (
          <div key={a.id} className={`gm-assessment-entry ${isCurrent ? 'gm-assessment-entry--pending' : ''}`}
            style={{ marginBottom: 8 }}>
            {/* Header */}
            <div className="gm-assessment-entry-header"
              onClick={() => setExpanded(prev => ({ ...prev, [a.id]: !prev[a.id] }))}>
              <div className="gm-assessment-entry-left">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span className={`gm-assessment-type-badge gm-assessment-type-badge--${a.type}`}>
                    {HISTORY_TYPE_LABELS[a.type]}
                    {a.period ? ` · ${a.period}` : ''}
                  </span>
                  {isCurrent && <span className="gm-pending-badge">Current</span>}
                  {isValidated && <span className="gm-validated-badge">✓ Validated</span>}
                  {a.status === 'submitted' && !isCurrent && <span className="gm-pending-badge">Pending review</span>}
                </div>
                <div className="gm-assessment-entry-stage" style={{ color, marginTop: 4 }}>{a.stage}</div>
                <div className="gm-assessment-entry-meta">
                  {formatDate(date)} · {a.stageUpgrade} · {a.stageProgress}% through stage
                </div>
              </div>
              <div className="gm-assessment-entry-right">
                <div className="gm-mini-progress" style={{ width: 100 }}>
                  <div className="gm-mini-progress-fill"
                    style={{ width: `${a.stageProgress||0}%`, background: color }} />
                </div>
                <span className="gm-session-toggle">{isExpanded ? '▲' : '▼'}</span>
              </div>
            </div>

            {/* Expanded answers */}
            {isExpanded && (
              <div className="gm-assessment-entry-body">
                {a.coachNotes && (
                  <div className="gm-coach-notes gm-coach-notes--highlighted" style={{ marginBottom: 16 }}>
                    <h3>💬 Coach feedback</h3>
                    <p style={{ whiteSpace: 'pre-wrap' }}>{a.coachNotes}</p>
                  </div>
                )}
                <div className="gm-section-title" style={{ fontSize: '0.75rem', marginBottom: 12 }}>Your answers</div>
                {QUESTIONS.map((q, idx) => {
                  const score = a.questionScores?.[q.id];
                  const answer = q.options.find(o => o.score === score)?.label || '—';
                  return (
                    <div key={q.id} className="gm-answer-row">
                      <span className="gm-answer-q">Q{String(idx+1).padStart(2,'0')} {q.text}</span>
                      <span className="gm-answer-v">{answer}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── New Assessment Picker ─────────────────────────────────────
function NewAssessmentPicker({ existingTypes, pickerType, setPickerType, pickerDate, setPickerDate, onStart }) {
  const TYPE_OPTIONS = [
    { value: 'baseline', label: 'Baseline — your starting point', disabledIf: existingTypes.includes('baseline') },
    { value: 'periodic', label: 'Periodic — a regular check-in', disabledIf: false },
    { value: 'target',   label: 'Target — your end-of-programme goal', disabledIf: existingTypes.includes('target') },
  ];

  const availableOptions = TYPE_OPTIONS.filter(o => !o.disabledIf);

  return (
    <div className="gm-section">
      <div className="gm-config-card">
        <div className="gm-config-label">Start a new assessment</div>
        <p className="gm-config-desc">
          Choose what kind of assessment this is, and the date it reflects.
        </p>
        <div style={{ display: 'flex', gap: 12, marginTop: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="gm-form-group" style={{ minWidth: 240 }}>
            <label>Assessment type</label>
            <select value={pickerType} onChange={e => setPickerType(e.target.value)}>
              <option value="">— Select type —</option>
              {availableOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="gm-form-group" style={{ minWidth: 180 }}>
            <label>Date</label>
            <input type="date" value={pickerDate} onChange={e => setPickerDate(e.target.value)}
              max={new Date().toISOString().slice(0,10)} />
          </div>
          <button className="gm-btn gm-btn-primary" onClick={onStart} disabled={!pickerType}>
            Start assessment
          </button>
        </div>
        {availableOptions.length === 0 && (
          <p style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: 8, fontStyle: 'italic' }}>
            You already have a Baseline and Target assessment. You can still create a new Periodic assessment using the option above.
          </p>
        )}
      </div>
    </div>
  );
}
